import { create } from 'zustand';

type JpegDownloadSelectionState = {
  isSelectionMode: boolean;
  studyInstanceUID: string | null;
  selectedImageIds: string[];
  setSelectionMode: (enabled: boolean) => void;
  toggleSelectionMode: () => void;
  clearSelection: () => void;
  isImageSelected: (imageId: string) => boolean;
  isDisplaySetSelected: (imageIds: string[]) => boolean;
  toggleImageIds: (imageIds: string[], studyInstanceUID: string) => void;
  addImageId: (imageId: string, studyInstanceUID: string) => void;
  removeImageId: (imageId: string) => void;
};

function uniqueImageIds(imageIds: string[]): string[] {
  return [...new Set(imageIds.filter(Boolean))];
}

export const useJpegDownloadSelectionStore = create<JpegDownloadSelectionState>((set, get) => ({
  isSelectionMode: false,
  studyInstanceUID: null,
  selectedImageIds: [],

  setSelectionMode: enabled =>
    set(state => ({
      isSelectionMode: enabled,
      ...(enabled ? {} : { selectedImageIds: [], studyInstanceUID: null }),
    })),

  toggleSelectionMode: () => {
    const nextMode = !get().isSelectionMode;
    get().setSelectionMode(nextMode);
  },

  clearSelection: () =>
    set({
      selectedImageIds: [],
      studyInstanceUID: null,
    }),

  isImageSelected: imageId => get().selectedImageIds.includes(imageId),

  isDisplaySetSelected: imageIds => {
    const normalized = uniqueImageIds(imageIds);
    if (!normalized.length) {
      return false;
    }

    const selected = get().selectedImageIds;
    return normalized.every(imageId => selected.includes(imageId));
  },

  toggleImageIds: (imageIds, studyInstanceUID) => {
    const normalized = uniqueImageIds(imageIds);
    if (!normalized.length) {
      return;
    }

    set(state => {
      const currentStudy = state.studyInstanceUID;
      const selected = currentStudy === studyInstanceUID ? [...state.selectedImageIds] : [];
      const allSelected = normalized.every(imageId => selected.includes(imageId));

      const nextSelected = allSelected
        ? selected.filter(imageId => !normalized.includes(imageId))
        : [...selected, ...normalized.filter(imageId => !selected.includes(imageId))];

      return {
        studyInstanceUID: nextSelected.length ? studyInstanceUID : null,
        selectedImageIds: nextSelected,
      };
    });
  },

  addImageId: (imageId, studyInstanceUID) => {
    if (!imageId) {
      return;
    }

    set(state => {
      const selected =
        state.studyInstanceUID === studyInstanceUID || !state.studyInstanceUID
          ? [...state.selectedImageIds]
          : [];

      if (selected.includes(imageId)) {
        return state;
      }

      return {
        studyInstanceUID,
        selectedImageIds: [...selected, imageId],
      };
    });
  },

  removeImageId: imageId =>
    set(state => {
      const nextSelected = state.selectedImageIds.filter(id => id !== imageId);
      return {
        selectedImageIds: nextSelected,
        studyInstanceUID: nextSelected.length ? state.studyInstanceUID : null,
      };
    }),
}));
